import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { UseHeadInput } from 'unhead/types';
import { useHead } from '@unhead/react';
import { CreateClientHeadOptions, createHead } from '@unhead/react/client';

import { trackPage } from 'utils/pushDataLayer';

export type AlternateLinks = Record<string, string | undefined>;

export type ToverHeadOptions = {
  title: string;
  description?: string;
  alternateLinks?: AlternateLinks;
  appName?: string;
  noIndex?: boolean;
};

export type CreateToverHeadOptions = CreateClientHeadOptions & { appName?: string };

let configuredAppName = '';

export function configureToverHead({ appName }: { appName: string }): void {
  configuredAppName = appName;
}

// Sets up unhead and the app-name suffix in one go: const head = createToverHead({ appName });
// then wrap the app root in <UnheadProvider value={head}>.
export function createToverHead({ appName = '', ...options }: CreateToverHeadOptions = {}) {
  configureToverHead({ appName });
  return createHead(options);
}

// Crawlers resolve a relative address against the page they found it on, so an address
// from the API ("category/article", no leading slash) must be made absolute here.
function absoluteHref(address: string): string {
  return new URL(address, window.location.origin).href;
}

// English is always the canonical version — search engines get one stable address per
// piece of content instead of whichever locale the reader happened to land on. Without an
// English translation it is the first translated address (the sitemap lists the same one),
// and only without any, the page's own.
function canonicalHref(alternateLinks?: AlternateLinks): string {
  const links = Object.entries(alternateLinks ?? {}).filter((entry): entry is [string, string] => !!entry[1]);
  const english = links.find(([locale]) => locale.toLowerCase().startsWith('en'));

  return absoluteHref((english ?? links[0])?.[1] ?? window.location.pathname);
}

// The address analytics knows a piece of content by, the same one canonicalHref picks.
function neutralAddress(alternateLinks?: AlternateLinks): string | undefined {
  const links = Object.entries(alternateLinks ?? {}).filter((entry): entry is [string, string] => !!entry[1]);

  return (links.find(([locale]) => locale.toLowerCase().startsWith('en')) ?? links[0])?.[1];
}

export function useToverHead({
  title,
  description,
  alternateLinks,
  appName = configuredAppName,
  noIndex = false,
}: ToverHeadOptions): void {
  const { t, i18n } = useTranslation();

  // A title is either a lang key or already the thing to show — an article's own
  // title, say — so only what the catalogue knows gets translated.
  const isKey = !!title && i18n.exists(title);
  const displayTitle = isKey ? t(title) : title;

  useHead({
    // No title yet (the content is still loading) leaves the default alone, instead of a
    // bare " | My Tover" that analytics would count as a page of its own.
    ...(displayTitle ? { title: displayTitle as string, titleTemplate: appName ? `%s | ${appName}` : '%s' } : {}),
    meta: [
      ...(description ? [{ name: 'description', content: description }] : []),
      ...(noIndex ? [{ name: 'robots', content: 'noindex' }] : []),
    ],
    link: (noIndex
      ? []
      : [
          { rel: 'canonical', href: canonicalHref(alternateLinks) },
          ...Object.entries(alternateLinks ?? {})
            .filter((entry): entry is [string, string] => !!entry[1])
            .map(([hreflang, href]) => ({ rel: 'alternate', hreflang, href: absoluteHref(href) })),
        ]) as UseHeadInput['link'],
  });

  // The page's name apart from the reader's language: the English title for what the
  // catalogue translates, the English address for content (only its own language has a title).
  const key = neutralAddress(alternateLinks) ?? window.location.pathname;
  const neutralTitle = neutralAddress(alternateLinks) ?? (isKey ? i18n.getFixedT('en')(title) : displayTitle);
  const language = useRef(i18n.language);
  language.current = i18n.language;

  useEffect(() => {
    if (!displayTitle || noIndex) return;

    trackPage({ title: neutralTitle as string, key, language: language.current });
  }, [displayTitle ? neutralTitle : '', key, noIndex]); // eslint-disable-line react-hooks/exhaustive-deps
}

export default useToverHead;
